import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Textarea } from "@/components/ui/textarea";
import {
  ChevronDown,
  ChevronUp,
  Copy,
  Clock,
  User,
  MapPin,
  Smartphone,
  AlertTriangle,
  CheckCircle,
  Edit3,
  Paperclip,
  Minimize2,
  Maximize2,
} from "lucide-react";

interface TicketSummaryProps {
  ticketId: string;
  summary: string;
  userInfo: {
    name: string;
    email: string;
    location?: string;
    accountStatus: "VIP" | "Premium" | "Free" | "Trial";
    sentiment: "Positive" | "Neutral" | "Negative" | "Frustrated";
  };
  timeline: {
    opened: string;
    lastResponse: string;
    responseTime?: string;
  };
  artifacts: {
    productVersion: string;
    platform: string;
    attachments?: number;
  };
  rootCause?: string;
  recommendations?: string[];
}

export function SupportTicketSummary({
  ticketId = "TKT-2024-001234",
  summary = "User experiencing login issues on mobile app after recent password reset. Unable to access account despite correct credentials. Issue appears to be related to session management on iOS devices. User has tried clearing cache and reinstalling app without success.",
  userInfo = {
    name: "Sarah Chen",
    email: "sarah.chen@company.com",
    location: "San Francisco, CA",
    accountStatus: "VIP",
    sentiment: "Frustrated",
  },
  timeline = {
    opened: "2024-01-15 09:23 AM",
    lastResponse: "2024-01-15 11:45 AM",
    responseTime: "2h 22m",
  },
  artifacts = {
    productVersion: "v2.4.1",
    platform: "iOS 17.2",
    attachments: 3,
  },
  rootCause = "Known issue with OAuth token refresh on iOS after password reset",
  recommendations = [
    "Clear app keychain data",
    "Send password reset link",
    "Escalate to iOS dev team if issue persists",
  ],
}: TicketSummaryProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editableSummary, setEditableSummary] = useState(summary);

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case "Positive":
        return "bg-green-100 text-green-800 border-green-200";
      case "Neutral":
        return "bg-gray-100 text-gray-800 border-gray-200";
      case "Negative":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "Frustrated":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getAccountStatusColor = (status: string) => {
    switch (status) {
      case "VIP":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "Premium":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "Free":
        return "bg-gray-100 text-gray-800 border-gray-200";
      case "Trial":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleSaveEdit = () => {
    setIsEditing(false);
    // Here you would typically save the edited summary to your backend
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          {isExpanded ? (
            <CardTitle className="text-lg font-semibold text-slate-900">
              Ticket Summary
            </CardTitle>
          ) : (
            <>
              {/* Summary Snippet */}
              <div className="bg-slate-50 p-3 rounded-md">
                <p className="text-sm text-slate-600 line-clamp-2">
                  {editableSummary.length > 120
                    ? `${editableSummary.substring(0, 120)}...`
                    : editableSummary}
                </p>
              </div>
            </>
          )}
          <div className="flex items-center gap-2">
            <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  {isExpanded ? (
                    <Maximize2 className="h-4 w-4" />
                  ) : (
                    <Minimize2 className="h-4 w-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
            </Collapsible>
          </div>
        </div>
      </CardHeader>

      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        {/* Collapsed State - Show Summary Snippet */}

        {/* Expanded State - Full Content */}
        <CollapsibleContent>
          <CardContent className="space-y-4">
            {/* Summary Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-slate-700">
                  Issue Summary
                </h3>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2"
                    onClick={() => copyToClipboard(editableSummary)}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {isEditing ? (
                <div className="space-y-2">
                  <Textarea
                    value={editableSummary}
                    onChange={(e) => setEditableSummary(e.target.value)}
                    className="min-h-[80px] text-sm"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleSaveEdit}>
                      Save
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setIsEditing(false);
                        setEditableSummary(summary);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 p-3 rounded-md">
                  {editableSummary}
                </p>
              )}
            </div>

            <Separator />

            {/* User Info Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-slate-700">
                User Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-slate-400" />
                  <div>
                    <p className="text-sm font-medium">{userInfo.name}</p>
                    <p className="text-xs text-slate-500">{userInfo.email}</p>
                  </div>
                </div>

                {userInfo.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    <p className="text-sm">{userInfo.location}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Badge
                  className={getAccountStatusColor(userInfo.accountStatus)}
                >
                  {userInfo.accountStatus}
                </Badge>
                <Badge className={getSentimentColor(userInfo.sentiment)}>
                  {userInfo.sentiment}
                </Badge>
              </div>
            </div>

            <Separator />

            {/* Timeline Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-slate-700">Timeline</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Opened</p>
                    <p className="font-medium">{timeline.opened}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Last Response</p>
                    <p className="font-medium">{timeline.lastResponse}</p>
                  </div>
                </div>

                {timeline.responseTime && (
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Response Time</p>
                      <p className="font-medium">{timeline.responseTime}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Artifacts Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-slate-700">
                Technical Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Platform</p>
                    <p className="text-sm font-medium">{artifacts.platform}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Version</p>
                    <p className="text-sm font-medium">
                      {artifacts.productVersion}
                    </p>
                  </div>
                </div>

                {artifacts.attachments && (
                  <div className="flex items-center gap-2">
                    <Paperclip className="h-4 w-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Attachments</p>
                      <p className="text-sm font-medium">
                        {artifacts.attachments} files
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Root Cause Section */}
            {rootCause && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    Likely Root Cause
                  </h3>
                  <p className="text-sm text-slate-600 bg-amber-50 p-3 rounded-md border border-amber-200">
                    {rootCause}
                  </p>
                </div>
              </>
            )}

            {/* Recommendations Section */}
            {recommendations && recommendations.length > 0 && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-slate-700">
                    Recommended Actions
                  </h3>
                  <ul className="space-y-2">
                    {recommendations.map((rec, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-2 text-sm"
                      >
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-600">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
